import * as React from 'react';
import { Component, Fragment } from 'react';
import { action, computed, observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import SwipeableViews from 'react-swipeable-views';
import _FundFacts from './FundFacts';
import FundCommentary from './FundCommentary';
import Chart from './Chart';
import KeyDocuments from './KeyDocuments';
import Disclaimers from './Disclaimers';
import withColumns from './withColumns';
import Tabs, { TabsProps, TabData } from './Tabs';
import ChartStore from '../stores/ChartStore';
import { camelCase } from '@kurtosys/ksys-app-template/dist/utils/changeCase';
import TabRegion from '../models/components/TabRegion';
import getTabPanelProps from '../utils/getTabPanelProps';

const FundFacts = withColumns(_FundFacts);

const FundCardMain = styled<any, any>('div')`
    ${propStyles};
`;

FundCardMain.defaultProps = {
    flex: '0 1 100%',
    minWidth: '0',
    paddingX: ['sm', 'md', 'lg'],
    paddingY: ['sm', 'md', 'lg'],
};

const SwipeyTabs = styled<any, any>(Tabs)`
    position: relative;
    flex-wrap: nowrap;
    overflow-x: auto;
    scroll-behavior: smooth;
    scroll-snap-type: mandatory;
    scroll-snap-destination: center;
    &::-webkit-scrollbar {
        display: none;
    }
`;

// Fixes issue with use of offsetHeight in react-swipeable-views
// Adding a wrapper with a border ensures offsetHeight value includes margin
const SwipeableViewWrapper = styled<any, any>('div')`
    border: 1px solid transparent;
`;

// Swipeable view style overrides for expanded view
const expandedRootStyle = {
    overflow: 'visible',
};

const expandedContainerStyle = {
    flexDirection: 'column',
    height: 'auto',
};

const expandedSlideStyle = {
    overflow: 'visible',
};

type TabDataWithConfig = TabData & { config?: any };

@inject('dataStore')
@observer
class FundCardTab extends Component<any, any> {
    @computed
    get tabsWithConfig(): Array<TabDataWithConfig> {
        const {
            hasFundCommentary,
            fundCommentary,
            fundFacts,
            charts,
            fundManagers,
            fundManagersConfig,
            fundManagersOrchestrationConfig,
            ratingImageStore,
            keyDocuments,
            keyDocumentsConfig,
        } = this.props;
        const tabs: Array<TabDataWithConfig> = [];

        if (hasFundCommentary) {
            this.addTab(tabs, {
                id: 'fundCommentary',
                label: fundCommentary.label || 'Commentary',
            });
        }

        if (Object.keys(fundFacts).length > 0) {
            this.addTab(tabs, {
                id: 'fundFacts',
                label: fundFacts.label,
            });
        }

        if (charts.length > 0) {
            const {
                dataStore,
                shareClass,
                isMobile,
                queryContext,
            } = this.props;

            // Iterate through charts to check if chartHasData
            charts.forEach(chartConfig => {
                const chartStore = new ChartStore(
                    dataStore.stores,
                    chartConfig,
                    queryContext,
                    shareClass,
                    isMobile
                );
                if (chartStore.displayChart) {
                    this.addTab(tabs, {
                        id: 'chart',
                        label: chartConfig.label,
                        config: chartConfig,
                    });
                }
            });
        }

        if (fundManagers.length > 0) {
            const config = fundManagersConfig;
            const tabManagersHeading =
                fundManagersOrchestrationConfig &&
                fundManagersOrchestrationConfig.mobileTabHeading;
            this.addTab(tabs, {
                id: 'fundManagers',
                label: tabManagersHeading || config.label,
            });
        }

        if (ratingImageStore.hasRatingImageOptions) {
            this.addTab(tabs, {
                id: 'ratingImage',
                label: ratingImageStore.headingText || 'Rating',
            });
        }

        if (keyDocuments.length > 0) {
            this.addTab(tabs, {
                id: 'keyDocuments',
                label: keyDocumentsConfig.label,
            });
        }

        return tabs;
    }

    @computed
    get tabs(): Array<TabData> {
        return this.tabsWithConfig.map((tab, index) => ({
            id: tab.id,
            label: tab.label,
        }));
    }

    @computed
    get tabsProps(): TabsProps {
        const { theme } = this.props;
        return {
            region: TabRegion.MobileFundCard,
            tabs: this.tabs,
            setTabActive: (tab, e) => this.setSelectedTab(e, tab.id),
            isTabSelected: tabId => tabId === this.selectedTab,
            getMetricContext: tab =>
                `fundCard-mobile-tab-${camelCase(tab.label)}`,
            tabThemeOverride: { flex: '0 0 auto', ...theme.FundCardMobileTab },
        };
    }

    @computed
    get selectedTabIndex(): number {
        const index = this.tabsWithConfig.findIndex(
            tab => tab.id === this.selectedTab
        );
        return index >= 0 ? index : 0;
    }
    @observable selectedTab: string;

    constructor(props: any) {
        super(props);
        if (this.tabsWithConfig) {
            const tab = this.tabs[0];
            if (tab) {
                this.selectedTab = tab.id;
            }
        }
    }

    addTab<T extends TabData>(tabs: Array<T>, tabData: T): void {
        tabData.id = `${tabData.id}-${tabs.length}`;
        tabs.push(tabData);
    }

    @action
    setSelectedTab = (tabNode, tabId: string) => {
        if (tabNode) {
            const tabLeft = tabNode.offsetLeft;
            const tabWidth = tabNode.offsetWidth;
            if (tabNode.parentNode) {
                const parentWidth = tabNode.parentNode.offsetWidth;
                tabNode.parentNode.scrollLeft =
                    tabLeft - (parentWidth - tabWidth) / 2;
            }
        }

        this.selectedTab = tabId;
    };

    @action
    setSelectedTabWithIndex = (index: number) => {
        const tab = this.tabsWithConfig[index];
        if (tab && tab.id) {
            this.setSelectedTab(null, tab.id);
        }
    };

    @action
    getFundCardComponent = (tabId, config) => {
        const {
            fundCommentary,
            fundFacts,
            fundManagersComponent,
            ratingImageStore,
            ratingImageComponent,
            keyDocumentsConfig,
            keyDocuments,
        } = this.props;
        const tabParts = tabId.split('-');
        const tabType = tabParts[0];

        const { dataStore, shareClass, isMobile, queryContext } = this.props;
        if (tabType === 'fundCommentary') {
            return (
                <FundCommentary
                    showHeading={dataStore.isExpandAll}
                    fundCommentary={fundCommentary}
                    shareClass={shareClass}
                />
            );
        }

        if (tabType === 'fundFacts') {
            return (
                <FundFacts
                    dataStore={dataStore}
                    showHeading={dataStore.isExpandAll}
                    fundFacts={fundFacts}
                    shareClass={shareClass}
                />
            );
        }

        if (
            ratingImageStore.ratingImagePosition === 'aboveFundManager' &&
            tabType === 'ratingImage'
        ) {
            return ratingImageComponent;
        }

        if (tabType === 'fundManagers') {
            return fundManagersComponent;
        }

        if (
            ratingImageStore.ratingImagePosition === 'belowFundManager' &&
            tabType === 'ratingImage'
        ) {
            return ratingImageComponent;
        }

        if (tabType === 'keyDocuments') {
            return (
                <KeyDocuments
                    showHeading={dataStore.isExpandAll}
                    keyDocumentsConfig={keyDocumentsConfig}
                    keyDocuments={keyDocuments}
                    shareClass={shareClass}
                    queryContext={queryContext}
                />
            );
        }

        if (tabType === 'chart') {
            const chartStore = new ChartStore(
                dataStore.stores,
                config,
                queryContext,
                shareClass,
                isMobile
            );
            return (
                <Chart
                    showHeading={dataStore.isExpandAll}
                    chartStore={chartStore}
                />
            );
        }

        return null;
    };

    render() {
        const {
            tabsWithConfig,
            selectedTabIndex,
            setSelectedTabWithIndex,
        } = this;
        const { theme, dataStore, fundCardDisclaimers } = this.props;
        return (
            <Fragment>
                {!dataStore.isExpandAll && (
                    <SwipeyTabs
                        {...this.tabsProps}
                        {...theme.FundCardMobileTabs}
                    />
                )}
                <FundCardMain {...theme.FundCardMobileMain}>
                    <SwipeableViews
                        disabled={dataStore.isExpandAll}
                        style={dataStore.isExpandAll ? expandedRootStyle : {}}
                        containerStyle={
                            dataStore.isExpandAll ? expandedContainerStyle : {}
                        }
                        slideStyle={
                            dataStore.isExpandAll ? expandedSlideStyle : {}
                        }
                        enableMouseEvents={true}
                        animateHeight={dataStore.isExpandAll ? false : true}
                        resistance={true}
                        index={selectedTabIndex}
                        onChangeIndex={index => setSelectedTabWithIndex(index)}
                    >
                        {tabsWithConfig.map((tab, index) => (
                            <SwipeableViewWrapper
                                key={tab.id}
                                {...getTabPanelProps(
                                    tab.id,
                                    TabRegion.MobileFundCard
                                )}
                            >
                                {this.getFundCardComponent(tab.id, tab.config)}
                            </SwipeableViewWrapper>
                        ))}
                    </SwipeableViews>
                </FundCardMain>
                <Disclaimers
                    disclaimers={fundCardDisclaimers}
                    marginX="sm"
                    theme={{
                        ...theme,
                        DisclaimersList: theme.FundCardDisclaimersList,
                        DisclaimerListItem: theme.FundCardDisclaimerListItem,
                        DisclaimerCharacter: theme.FundCardDisclaimerCharacter,
                        DisclaimerContent: theme.FundCardDisclaimerContent,
                    }}
                />
            </Fragment>
        );
    }
}

export default withTheme(FundCardTab) as any;



// WEBPACK FOOTER //
// ./src/Components/FundCardTab.tsx